// JWT工具函数

export interface JWTPayload {
  internal_token?: string
  tenantId?: number
  userId?: number
  sub?: string
  username?: string
  email?: string
  avatar?: string
  iat?: number
  exp?: number
}

/**
 * 解析JWT token
 * @param token JWT token字符串
 * @returns 解析后的payload对象
 */
export function parseJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }
    
    const payload = parts[1]
    const decoded = atob(payload)
    return JSON.parse(decoded)
  } catch (error) {
    console.error('Failed to parse JWT token:', error)
    return null
  }
}

/**
 * 检查JWT token是否过期
 * @param token JWT token字符串
 * @returns 是否过期
 */
export function isTokenExpired(token: string): boolean {
  const payload = parseJWT(token)
  if (!payload || !payload.exp) {
    return true
  }
  
  const currentTime = Math.floor(Date.now() / 1000)
  return payload.exp < currentTime
}

/**
 * 获取token过期时间
 * @param token JWT token字符串
 * @returns 过期时间戳（秒）
 */
export function getTokenExpiration(token: string): number | null {
  const payload = parseJWT(token)
  return payload?.exp || null
}

/**
 * 从JWT token中提取用户信息
 * @param token JWT token字符串
 * @returns 用户信息对象
 */
export function extractUserFromToken(token: string) {
  const payload = parseJWT(token)
  if (!payload) {
    return null
  }
  
  return {
    id: payload.userId?.toString() || payload.sub?.toString() || "",
    username: payload.username || "用户",
    email: payload.email,
    avatar: payload.avatar,
    tenantId: payload.tenantId,
    internalToken: payload.internal_token
  }
}

/**
 * 检查token是否即将过期（默认5分钟内）
 * @param token JWT token字符串
 * @param threshold 提前时间（秒），默认300秒
 * @returns 是否即将过期
 */
export function isTokenExpiringSoon(token: string, threshold: number = 300): boolean {
  const payload = parseJWT(token)
  if (!payload || !payload.exp) {
    return true
  }
  
  const currentTime = Math.floor(Date.now() / 1000)
  return payload.exp - currentTime < threshold
} 